import { useAccount } from "@qw/auth";
import { useChatSessionCompletedSubscription, useChatSessionNotificationMetadataLazyQuery } from "@qw/graphql";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";

import { completedResponsesAtom } from "../atoms";
import { useUnreadSessions } from "../hooks/use-unread-sessions";
import { useWindowVisibility } from "../hooks/use-window-visibility";
import { isDocumentVisible, sendNotification } from "../utils/browser-notifications";

const NOTIFIED_RESPONSES_KEY = "chat-notified-responses";
const MAX_RETRY_ATTEMPTS = 3;

const getNotifiedResponses = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem(NOTIFIED_RESPONSES_KEY);
    return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
  } catch {
    return new Set();
  }
};

const addNotifiedResponse = (messageId: string): void => {
  try {
    const responses = getNotifiedResponses();
    responses.add(messageId);
    const arr = [...responses];
    if (arr.length > 100) {
      sessionStorage.setItem(NOTIFIED_RESPONSES_KEY, JSON.stringify(arr.slice(-50)));
    } else {
      sessionStorage.setItem(NOTIFIED_RESPONSES_KEY, JSON.stringify(arr));
    }
  } catch {
    // Ignore storage errors.
  }
};

export interface ChatNotificationClickContext {
  inboxConversationId?: string | null;
  insuredId?: string | null;
  messageId: string;
  opportunityId?: string | null;
  sessionId: string;
}

interface ChatNotificationListenerProps {
  alertFaviconHref?: string;
  onNotificationClick?: (context: ChatNotificationClickContext) => void;
}

export const ChatNotificationListener = ({ alertFaviconHref, onNotificationClick }: ChatNotificationListenerProps) => {
  const { user } = useAccount();
  const [completedResponses, setCompletedResponses] = useAtom(completedResponsesAtom);
  const { activeChatSessionId, markSessionAsUnread } = useUnreadSessions();
  const [fetchNotificationMetadata] = useChatSessionNotificationMetadataLazyQuery();
  const isWindowVisible = useWindowVisibility();

  const activeChatSessionIdRef = useRef(activeChatSessionId);
  const originalTitleRef = useRef<string>("");
  const originalFaviconRef = useRef<string>("");
  const isNotifyingRef = useRef(false);
  const [retryTrigger, setRetryTrigger] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousVisibilityRef = useRef(isWindowVisible);

  useEffect(() => {
    activeChatSessionIdRef.current = activeChatSessionId;
  }, [activeChatSessionId]);

  const showTabNotification = () => {
    if (isNotifyingRef.current) {
      return;
    }

    originalTitleRef.current = document.title;
    document.title = `Alby's Done! - ${originalTitleRef.current}`;
    isNotifyingRef.current = true;

    if (!alertFaviconHref) {
      return;
    }

    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) {
      return;
    }

    originalFaviconRef.current = favicon.href;
    favicon.href = alertFaviconHref;
  };

  const clearTabNotification = () => {
    if (!isNotifyingRef.current) {
      return;
    }

    if (document.title.startsWith("Alby's Done! - ")) {
      document.title = originalTitleRef.current;
    }
    originalTitleRef.current = "";

    if (originalFaviconRef.current) {
      const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (favicon && alertFaviconHref && favicon.href.includes(alertFaviconHref)) {
        favicon.href = originalFaviconRef.current;
      }
      originalFaviconRef.current = "";
    }

    isNotifyingRef.current = false;
  };

  useEffect(() => {
    const previousVisibility = previousVisibilityRef.current;
    previousVisibilityRef.current = isWindowVisible;

    if (isWindowVisible && isNotifyingRef.current) {
      clearTabNotification();
    }

    if (previousVisibility && !isWindowVisible && completedResponses.length > 0) {
      setTimeout(() => {
        setRetryTrigger((prev) => prev + 1);
      }, 0);
    }
  }, [isWindowVisible, completedResponses.length]);

  useEffect(() => {
    if (!activeChatSessionId || !isWindowVisible) {
      return;
    }

    try {
      localStorage.setItem(
        "chat-viewed-session",
        JSON.stringify({
          sessionId: activeChatSessionId,
          timestamp: Date.now(),
        })
      );
      localStorage.removeItem("chat-viewed-session");
    } catch {
      // Ignore storage errors.
    }
  }, [activeChatSessionId, isWindowVisible]);

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== "chat-viewed-session" || !event.newValue) {
        return;
      }

      try {
        JSON.parse(event.newValue);
        if (isNotifyingRef.current) {
          clearTabNotification();
        }
      } catch {
        // Ignore parse errors.
      }
    };

    globalThis.addEventListener("storage", handleStorageEvent);
    return () => {
      globalThis.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (isNotifyingRef.current) {
        clearTabNotification();
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  useChatSessionCompletedSubscription({
    variables: { userId: user?.id },
    skip: !user?.id,
    onData: ({ data }: { data: { data?: { chatSessionCompleted?: { chatSessionId?: string } } } }) => {
      const completedSessionId = data.data?.chatSessionCompleted?.chatSessionId;
      if (!completedSessionId) {
        return;
      }

      const currentActiveChatSessionId = activeChatSessionIdRef.current;
      if (currentActiveChatSessionId !== completedSessionId || !isDocumentVisible()) {
        markSessionAsUnread(completedSessionId);
      }
    },
  });

  const completedResponsesRef = useRef(completedResponses);

  useEffect(() => {
    completedResponsesRef.current = completedResponses;
  }, [completedResponses]);

  const completedMessageIds = useMemo(
    () =>
      [...completedResponses]
        .map((response) => response.messageId)
        .sort()
        .join(","),
    [completedResponses]
  );

  useEffect(() => {
    let cancelled = false;

    const processNotifications = async () => {
      const responses = completedResponsesRef.current;
      if (responses.length === 0) {
        return;
      }

      const notifiedResponses = getNotifiedResponses();
      const processedMessageIds = new Set<string>();
      const responsesToRetry = new Map<string, number>();

      for (const response of responses) {
        if (cancelled) {
          return;
        }

        const { sessionId, messageId, retryCount = 0 } = response;

        if (notifiedResponses.has(messageId) || retryCount >= MAX_RETRY_ATTEMPTS) {
          processedMessageIds.add(messageId);
          continue;
        }

        const isViewingThisSession = activeChatSessionIdRef.current === sessionId;
        if (isDocumentVisible() && isViewingThisSession) {
          addNotifiedResponse(messageId);
          processedMessageIds.add(messageId);
          continue;
        }

        try {
          const { data } = await fetchNotificationMetadata({
            variables: { chatSessionId: sessionId },
          });

          if (cancelled) {
            return;
          }

          if (!data?.chatSessionNotificationMetadata) {
            responsesToRetry.set(messageId, retryCount + 1);
            continue;
          }

          const { displayTitle, inboxConversationId, insuredId, opportunityId } = data.chatSessionNotificationMetadata;
          const notification = await sendNotification({
            title: "Alby has finished responding",
            body: displayTitle,
            tag: `chat-${messageId}`,
            requireInteraction: false,
            onClick: () => {
              window.focus();
              onNotificationClick?.({
                inboxConversationId,
                insuredId,
                messageId,
                opportunityId,
                sessionId,
              });
            },
          });

          if (cancelled) {
            return;
          }

          const documentVisible = isDocumentVisible();
          if (!documentVisible) {
            showTabNotification();
          }

          markSessionAsUnread(sessionId);

          if (notification || !documentVisible) {
            addNotifiedResponse(messageId);
            processedMessageIds.add(messageId);
          } else {
            responsesToRetry.set(messageId, retryCount + 1);
          }
        } catch {
          responsesToRetry.set(messageId, retryCount + 1);
        }
      }

      if (cancelled) {
        return;
      }

      setCompletedResponses((current) =>
        current
          .filter((response) => !processedMessageIds.has(response.messageId))
          .map((response) =>
            responsesToRetry.has(response.messageId)
              ? { ...response, retryCount: responsesToRetry.get(response.messageId) }
              : response
          )
      );

      if (responsesToRetry.size === 0) {
        return;
      }

      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }

      const maxRetryCount = Math.max(...responsesToRetry.values());
      const backoffMs = Math.min(1000 * 2 ** (maxRetryCount - 1), 4000);

      retryTimerRef.current = setTimeout(() => {
        setRetryTrigger((prev) => prev + 1);
      }, backoffMs);
    };

    void processNotifications();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [
    completedMessageIds,
    retryTrigger,
    fetchNotificationMetadata,
    markSessionAsUnread,
    onNotificationClick,
    setCompletedResponses,
  ]);

  return null;
};
